import { useEffect, useState } from "react";
import { Button, Card, Heading, Pill, Stack } from "../ui/primitives.js";
import { getCrashLog, type CrashReport } from "./crashLog.js";

/**
 * In-app crash log viewer. Reachable from the Home footer; intended
 * for support flows on sideloaded tablets where dev tools aren't
 * available. Shows the last 50 captured crashes, lets the user
 * download them as JSON, optionally upload to the worker, or clear.
 */
export function Diagnostics(): JSX.Element {
  const [reports, setReports] = useState<CrashReport[]>(() =>
    getCrashLog().list(),
  );
  const [uploadStatus, setUploadStatus] = useState<string | null>(null);

  useEffect(() => {
    const stop = getCrashLog().subscribe(setReports);
    return stop;
  }, []);

  const downloadJson = () => {
    const blob = new Blob([getCrashLog().exportJson()], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `radar-sim-crash-log-${new Date().toISOString().replace(/[:.]/g, "-")}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const upload = async () => {
    const url = uploadEndpoint();
    if (url === null) {
      setUploadStatus(
        "No upload endpoint configured. Use the Download button instead.",
      );
      return;
    }
    setUploadStatus("Uploading…");
    try {
      const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: getCrashLog().exportJson(),
      });
      setUploadStatus(
        res.ok
          ? `Uploaded ${reports.length} report(s). Thank you.`
          : `Upload failed: ${res.status} ${res.statusText}`,
      );
    } catch (e) {
      setUploadStatus(`Upload failed: ${(e as Error).message}`);
    }
  };

  const clear = () => {
    if (
      window.confirm(
        `Clear all ${reports.length} crash report(s) from this device?`,
      )
    ) {
      getCrashLog().clear();
      setUploadStatus(null);
    }
  };

  return (
    <div
      style={{
        width: "100%",
        maxWidth: 980,
        margin: "0 auto",
        padding: "16px 24px 64px",
        display: "flex",
        flexDirection: "column",
        gap: 16,
      }}
    >
      <Stack gap={6}>
        <Heading level={1} style={{ fontSize: 24 }}>
          Diagnostics
        </Heading>
        <p style={{ margin: 0, color: "var(--text-muted)", fontSize: 14 }}>
          Recent crashes captured on this device. Stored locally — never
          uploaded automatically.
        </p>
      </Stack>

      <Stack direction="row" gap={10} wrap align="center">
        <Pill tone={reports.length === 0 ? "success" : "danger"}>
          {reports.length} {reports.length === 1 ? "report" : "reports"}
        </Pill>
        <Button onClick={downloadJson} disabled={reports.length === 0}>
          Download JSON
        </Button>
        <Button
          variant="secondary"
          onClick={upload}
          disabled={reports.length === 0}
        >
          Upload to support
        </Button>
        <Button
          variant="danger"
          onClick={clear}
          disabled={reports.length === 0}
        >
          Clear log
        </Button>
        {uploadStatus !== null && (
          <span style={{ fontSize: 12, color: "var(--text-muted)" }}>
            {uploadStatus}
          </span>
        )}
      </Stack>

      {reports.length === 0 ? (
        <Card>
          <p style={{ margin: 0, color: "var(--text-muted)", fontSize: 14 }}>
            No crashes captured. The app is running cleanly on this device.
          </p>
        </Card>
      ) : (
        <Stack gap={12}>
          {[...reports].reverse().map((r) => (
            <CrashCard key={r.id} report={r} />
          ))}
        </Stack>
      )}
    </div>
  );
}

function CrashCard(props: { report: CrashReport }): JSX.Element {
  const r = props.report;
  return (
    <Card padding={16}>
      <Stack gap={10}>
        <Stack direction="row" gap={8} wrap align="center">
          <Pill tone={toneFor(r.source)}>{r.source}</Pill>
          <span
            style={{
              fontSize: 12,
              color: "var(--text-muted)",
              fontFamily: "var(--font-mono)",
            }}
          >
            {new Date(r.timestamp).toLocaleString()}
          </span>
          <span
            style={{
              fontSize: 11,
              color: "var(--text-dim)",
              fontFamily: "var(--font-mono)",
              marginLeft: "auto",
            }}
          >
            {r.id}
          </span>
        </Stack>
        <div
          style={{
            fontSize: 13,
            fontWeight: 600,
            color: "var(--text)",
            wordBreak: "break-word",
          }}
        >
          {r.message}
        </div>
        {r.url !== undefined && (
          <div
            style={{
              fontSize: 12,
              color: "var(--text-muted)",
              fontFamily: "var(--font-mono)",
              wordBreak: "break-all",
            }}
          >
            {r.url}
          </div>
        )}
        {r.stack !== undefined && r.stack !== "" && (
          <details>
            <summary
              style={{
                fontSize: 12,
                color: "var(--text-muted)",
                cursor: "pointer",
              }}
            >
              Stack trace
            </summary>
            <pre style={preStyle}>{r.stack}</pre>
          </details>
        )}
        {r.componentStack !== undefined && r.componentStack !== "" && (
          <details>
            <summary
              style={{
                fontSize: 12,
                color: "var(--text-muted)",
                cursor: "pointer",
              }}
            >
              React component stack
            </summary>
            <pre style={preStyle}>{r.componentStack}</pre>
          </details>
        )}
        {r.context !== undefined && Object.keys(r.context).length > 0 && (
          <details>
            <summary
              style={{
                fontSize: 12,
                color: "var(--text-muted)",
                cursor: "pointer",
              }}
            >
              Context
            </summary>
            <pre style={preStyle}>{JSON.stringify(r.context, null, 2)}</pre>
          </details>
        )}
      </Stack>
    </Card>
  );
}

function toneFor(source: CrashReport["source"]): "neutral" | "danger" | "accent" {
  switch (source) {
    case "render":
      return "danger";
    case "window-error":
      return "danger";
    case "unhandled-rejection":
      return "accent";
    case "manual":
      return "neutral";
  }
}

const preStyle: React.CSSProperties = {
  fontFamily: "var(--font-mono)",
  fontSize: 11,
  padding: 10,
  borderRadius: "var(--radius-sm)",
  background: "var(--bg-elev)",
  color: "var(--text)",
  overflow: "auto",
  maxHeight: 240,
  whiteSpace: "pre-wrap",
  wordBreak: "break-word",
  margin: "6px 0 0",
};

function uploadEndpoint(): string | null {
  if (typeof window === "undefined") return null;
  const w = window as unknown as {
    __RADAR_SIM_CRASH_REPORT_URL__?: string;
    __RADAR_SIM_API__?: string;
  };
  if (typeof w.__RADAR_SIM_CRASH_REPORT_URL__ === "string") {
    return w.__RADAR_SIM_CRASH_REPORT_URL__;
  }
  // Default to the worker on the same origin (production) or localhost
  // (dev). Matches the `DEFAULT_API` resolution rule in the classroom
  // client; kept duplicated here to avoid an import cycle.
  const base =
    window.location.hostname === "localhost"
      ? "http://localhost:8787"
      : "";
  return `${base}/diagnostics/crash-log`;
}
